import {
  TimelineStart,
  StepIndicator,
  TimelineIndicator,
} from "~/molecules/WorkflowTimelineIndicators";
import { useTranslation } from "react-i18next";
import {
  FetchError,
  WorkflowStepStatuses,
  type DocumentDetailsVersion,
  type WorkflowDetails,
} from "~/api/types";
import { Link } from "react-router";
import { Button } from "~/atoms/button";
import { Archive, BookLock, CheckCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { formatDate, toISOString } from "~/helpers/date";
import { SetAdoptionDateModal } from "~/molecules/SetAdoptionDateModal";
import { PublishModal } from "~/molecules/PublishModal";
import { WorkflowOverviewStepControlContainer } from "~/molecules/WorkflowOverviewStepControlContainer";
import { WorkflowOverviewStep } from "~/molecules/WorkflowOverviewStep";
import WorkflowDescription from "~/molecules/WorkflowDescription";
import {
  canEditWorkflow,
  canManageVersion,
  canPublishVersion,
  canSetWorkflowAdoptionDate,
  getApprovalStatus,
  getWorkflowStepControlPermissions,
  isDraftVersion,
} from "~/helpers/documentPermissions";
import { updateDocument, updateDocumentVersion } from "~/api/v2/requests";
import { toast } from "react-toastify";
import NoResult from "~/molecules/NoResult";

interface Props {
  workflow?: WorkflowDetails;
  documentId: number;
  selectedVersion: DocumentDetailsVersion;
  onPublish: (versionId: number) => void;
  onChange: () => void;
}

export const WorkflowOverview = ({
  workflow,
  documentId,
  selectedVersion,
  onPublish,
  onChange,
}: Props) => {
  const { t } = useTranslation();

  const isDraft = isDraftVersion(selectedVersion);

  const [isWorkflowExpanded, setIsWorkflowExpanded] = useState(isDraft);

  useEffect(() => {
    setIsWorkflowExpanded(isDraft);
  }, [isDraft]);

  const canEditCurrentWorkflow = canEditWorkflow(selectedVersion);
  const canManage = canManageVersion(selectedVersion);

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishError, setPublishError] = useState<FetchError | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);

  if (workflow === undefined) {
    return <NoResult />;
  }

  const lastStep = workflow.steps[workflow.steps.length - 1];
  const lastStepIndex = workflow.steps.length - 1;
  const isLastStepActive = lastStep?.status === WorkflowStepStatuses.active;

  const showSetAdoptionDate = canSetWorkflowAdoptionDate(
    selectedVersion,
    isLastStepActive,
  );

  const { showPublishButton } = getWorkflowStepControlPermissions({
    canManage,
    canPublish: canPublishVersion(selectedVersion),
    isWorkflowStarted: workflow.isOngoing,
    isActiveStep: isLastStepActive,
    isFirstStep: lastStepIndex === 0,
    isLastStep: true,
    versionStatus: selectedVersion.status,
  });

  const { isWaitingForApprovals, approvedCount, totalCount } = lastStep
    ? getApprovalStatus(lastStep)
    : { isWaitingForApprovals: false, approvedCount: 0, totalCount: 0 };

  const handlePublishDocument = async (params: {
    scheduleReviewFrom?: Date | null;
    scheduleReviewTo?: Date | null;
    scheduleReviewPeriod?: string | null;
  }) => {
    setIsPublishing(true);
    setPublishError(null);

    try {
      await updateDocumentVersion(selectedVersion.id, {
        status: "published",
      });

      toast.success(t(($) => $.commons.workflow.documentPublishedSuccessfully));

      if (canManage) {
        try {
          await updateDocument(documentId, {
            scheduleReviewFrom: toISOString(params.scheduleReviewFrom),
            scheduleReviewTo: toISOString(params.scheduleReviewTo),
            scheduleReviewPeriod: params.scheduleReviewPeriod,
          });
        } catch {
          setShowPublishModal(false);
          toast.warning(
            t(($) => $.commons.workflow.reviewScheduleCouldNotBeUpdated),
          );
          onPublish(selectedVersion.id);
          return;
        }
      }

      setShowPublishModal(false);
      onPublish(selectedVersion.id);
    } catch (error) {
      if (error instanceof FetchError) {
        setPublishError(error);
      }
      if (
        !(error instanceof FetchError) ||
        error.code !== "DOCUMENT_OPEN_IN_EDITOR"
      ) {
        toast.error(t(($) => $.commons.workflow.failedToPublishDocument));
      }
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {canEditCurrentWorkflow && (
        <div className="flex justify-end">
          <Button variant="outline" asChild>
            <Link to={`/workflows/${workflow.id}/edit`}>
              {t(($) => $.commons.workflow.editWorkflow)}
            </Link>
          </Button>
        </div>
      )}
      <WorkflowDescription description={workflow.description} />
      <div className="relative">
        <div className="bg-primary/40 absolute top-0 bottom-0 left-6 z-1 w-0.5"></div>
        <div className="space-y-4">
          <TimelineStart
            startedAt={workflow.stepInstances[0]?.createdAt}
            scheduleReviewFrom={workflow.document.scheduleReviewFrom}
          />

          {isWorkflowExpanded ? (
            workflow.steps.map((step, index) => (
              <WorkflowOverviewStepControlContainer
                key={step.id}
                step={step}
                stepIndex={index}
                totalSteps={workflow.steps.length}
                workflowId={workflow.id}
                selectedVersion={selectedVersion}
                isWorkflowStarted={workflow.isOngoing}
                onChange={onChange}
              >
                <div className="flex items-start gap-2 py-2">
                  <StepIndicator status={step.status} />
                  <div className="min-w-0 grow">
                    <WorkflowOverviewStep
                      step={step}
                      stepInstances={workflow.stepInstances}
                      selectedVersionStatus={selectedVersion.status}
                      onUserResponse={onChange}
                    />
                  </div>
                </div>
              </WorkflowOverviewStepControlContainer>
            ))
          ) : (
            <div className="flex items-center gap-5 py-2">
              <StepIndicator
                value="..."
                status={WorkflowStepStatuses.upcoming}
              />
              <div className="flex grow items-center text-sm italic">
                {workflow.steps.length}{" "}
                {t(($) => $.commons.workflow.completedSteps)}
              </div>
            </div>
          )}

          {!isDraft && (
            <div className="flex justify-center">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsWorkflowExpanded(!isWorkflowExpanded)}
              >
                {isWorkflowExpanded
                  ? t(($) => $.commons.workflow.hideSteps)
                  : t(($) => $.commons.workflow.showSteps)}
              </Button>
            </div>
          )}

          {selectedVersion.publishedAt && (
            <TimelineIndicator
              icon={CheckCircle}
              text={`${t(($) => $.commons.workflow.published)} ${formatDate(selectedVersion.publishedAt, "date")}`}
            />
          )}

          {showSetAdoptionDate && (
            <TimelineIndicator
              icon={BookLock}
              text={`${t(($) => $.commons.document.adopted)} ${formatDate(selectedVersion.adoptedAt, "date")}`}
              variant={selectedVersion.adoptedAt ? "primary" : "gray"}
            />
          )}
          {showSetAdoptionDate && (
            <div className="ml-12 flex justify-center">
              <SetAdoptionDateModal
                adoptedAt={selectedVersion.adoptedAt}
                documentVersionId={selectedVersion.id}
                onChange={onChange}
              />
            </div>
          )}

          {showPublishButton && (
            <div className="ml-12 flex justify-center">
              <PublishModal
                open={showPublishModal}
                onOpenChange={(open) => {
                  setShowPublishModal(open);
                  if (!open) setPublishError(null);
                }}
                onConfirm={handlePublishDocument}
                documentId={documentId}
                loading={isPublishing}
                disabled={isWaitingForApprovals}
                buttonText={
                  isWaitingForApprovals
                    ? t(($) => $.commons.workflow.waitingForApprovals, {
                        approvedCount,
                        totalCount,
                      })
                    : t(($) => $.commons.workflow.publishDocument)
                }
                canManage={canManage}
                versionFileType={selectedVersion.fileType}
                versionId={selectedVersion.id}
                error={publishError}
              />
            </div>
          )}

          {!isDraft ? (
            <TimelineIndicator
              icon={Archive}
              text={`${t(($) => $.commons.documentStatus.archived)} ${formatDate(selectedVersion.archivedAt, "date")}`}
              variant={selectedVersion.archivedAt ? "primary" : "gray"}
            />
          ) : (
            <TimelineIndicator
              icon={CheckCircle}
              text={
                workflow.documentVersion.reviewTo
                  ? `${t(($) => $.commons.workflow.due)} ${formatDate(workflow.documentVersion.reviewTo, "date")}`
                  : undefined
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};
